import { useAtomValue } from "jotai";
import {
  type AccountEntity,
  AccountEntityType,
  type WorkspaceEntity,
} from "@/api/__generated__/orval/accounts";
import {
  PermissionItem,
  type UserEntity,
} from "@/api/__generated__/orval/auth";
import { UserReadDtoRoleEnum } from "@/config/apiClient";
import { currentWorkspaceAtom, userAtom } from "@/store";
import { currentAccountAtom } from "@/store/atoms/currentAccount";

const buildPermissionTree = (
  user: UserEntity | null | undefined,
  currentAccount: AccountEntity | undefined,
  currentWorkspace: WorkspaceEntity | undefined,
  organizationId?: string
) => {
  if (!user || !currentAccount) {
    return {
      accPerms: undefined,
      orgPerms: undefined,
      wrkPerms: undefined,
    };
  }

  // Case broker partnership
  if (currentAccount?.id !== currentWorkspace?.accountId) {
    return {
      accPerms: user.permissions[currentAccount.id] || undefined,
      orgPerms: organizationId
        ? user.permissions[currentAccount.id]?.organizations?.[organizationId]
        : Object.values(
            user.permissions[currentAccount.id]?.organizations || {}
          )[0] || undefined,
      wrkPerms: currentWorkspace?.id
        ? user.permissions[currentWorkspace.accountId]?.organizations?.[
            currentWorkspace.organizationId
          ]?.workspaces?.[currentWorkspace.id]
        : undefined,
    };
  }

  return {
    accPerms: user.permissions[currentWorkspace.accountId],
    orgPerms:
      user.permissions[currentWorkspace.accountId]?.organizations?.[
        currentWorkspace.organizationId
      ],
    wrkPerms:
      user.permissions[currentWorkspace.accountId]?.organizations?.[
        currentWorkspace.organizationId
      ]?.workspaces?.[currentWorkspace.id],
  };
};

export const useRolePermission = (organizationId?: string) => {
  const user = useAtomValue(userAtom);
  const currentAccount = useAtomValue(currentAccountAtom);
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const permissionsTree = buildPermissionTree(
    user,
    currentAccount,
    currentWorkspace,
    organizationId
  );

  const userHasRole = (role: UserReadDtoRoleEnum) =>
    [
      permissionsTree?.accPerms?.roleType,
      permissionsTree?.orgPerms?.roleType,
      permissionsTree?.wrkPerms?.roleType,
    ].includes(role);

  const userHasPermission = (
    permission: PermissionItem,
    subjectId?: string
  ): boolean => {
    // TODO : Refact
    const adminPermissions: PermissionItem[] = [
      PermissionItem.accountsaccountsmanagement,
      PermissionItem.accountsaccountsread,
      PermissionItem.accountsorganizationsmanagement,
      PermissionItem.accountsorganizationsread,
      PermissionItem.accountsworkspacesmanagement,
      PermissionItem.accountsworkspacesread,
      PermissionItem.authrolesmanagement,
      PermissionItem.authusersmanagement,
      PermissionItem.authusersread,
      PermissionItem.authrolesread,
      PermissionItem.authapikeymanagement,
      PermissionItem.authapikeyread,
    ];

    if (subjectId) {
      if (subjectId.startsWith("acc_")) {
        return (
          permissionsTree.accPerms?.permissions?.includes(permission) ?? false
        );
      }

      if (subjectId.startsWith("org_")) {
        return [
          ...(permissionsTree.accPerms?.permissions || []),
          ...(permissionsTree.orgPerms?.permissions || []),
        ].includes(permission);
      }
    }

    // Check if the permission is an admin permission
    if (adminPermissions.includes(permission)) {
      return [
        ...(permissionsTree.accPerms?.permissions || []),
        ...(permissionsTree.orgPerms?.permissions || []),
        ...(permissionsTree.wrkPerms?.permissions || []),
      ].includes(permission);
    } else {
      // Check if the user has an advertiser role
      if (hasAdvertiserRole) {
        return [
          ...(permissionsTree.accPerms?.permissions || []),
          ...(permissionsTree.orgPerms?.permissions || []),
          ...(permissionsTree.wrkPerms?.permissions || []),
        ].includes(permission);
      } else {
        // Check if the user has a broker role
        return [...(permissionsTree.wrkPerms?.permissions || [])].includes(
          permission
        );
      }
    }
  };

  // Account type flags
  const isBrokerAccount = currentAccount?.type === AccountEntityType.BROKER;
  const isKazaarAccount = currentAccount?.type === AccountEntityType.KAZAAR;
  const isAdvertiserAccount =
    currentAccount?.type === AccountEntityType.ADVERTISER;

  // Role flags
  const hasKazaarRole = userHasRole(UserReadDtoRoleEnum.Kazaar);
  const hasBrokerRole = userHasRole(UserReadDtoRoleEnum.Broker);
  const hasSupplierRole = userHasRole(UserReadDtoRoleEnum.Supplier);
  const hasAdvertiserRole = userHasRole(UserReadDtoRoleEnum.Advertiser);

  return {
    userHasRole,
    userHasPermission,
    // Account type flags
    isAdvertiserAccount,
    isBrokerAccount,
    isKazaarAccount,
    // Role flags
    hasKazaarRole,
    hasBrokerRole,
    hasAdvertiserRole,
    hasSupplierRole,
    hasBrokerKazaarRole: hasBrokerRole || hasKazaarRole,
    hasAdvertiserKazaarRole: hasAdvertiserRole || hasKazaarRole,
  };
};
